import React, { useCallback, useMemo, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import { Button, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaRegClock } from 'react-icons/fa';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { MdWarning, MdInfo, MdCheckCircle, MdErrorOutline, MdDelete, MdZoomIn, MdClose } from 'react-icons/md';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import { GoToChannel } from '@/_elements/Links';
import html2canvas from 'html2canvas';
import { VscLoading } from 'react-icons/vsc';
import { ChartCard, ChartContainer, ValidationBadge, ZoomModal } from './SingleChartContainer.styles';
import ModalHeader from 'react-bootstrap/esm/ModalHeader';
import { ChartDetailsFooter, CustomLegend, CustomTooltip } from './CustomRechartsComponents';
import UpdateChannelChartModal from './UpdateChannelChartModal';
import { BsPencil } from 'react-icons/bs';

const CHART_COLORS = [
    '#50AEB0',
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff8042',
    '#a4de6c',
    '#d0ed57',
    '#83a6ed',
    '#8dd1e1',
    '#a4de6c',
    '#d0ed57',
    '#ffc658',
];

const ChartRenderer = React.memo(
    ({
        chartType,
        subTitle: _subTitle,
        data,
        dataKeys,
        height = 300,
        interactive = false,
        zoom,
        chart,
        preview,
        onCellClick,
        previewColorMap = {},
    }) => {
        const cfgColorMap = useMemo(() => {
            return ((chart || {})?.config || {}).colorMap || {};
        }, [chart]);

        const mergedColorMap = useMemo(() => {
            return { ...cfgColorMap, ...previewColorMap };
        }, [cfgColorMap, previewColorMap]);
        const getDataKey = (dataKeyObj) => dataKeyObj?.key || dataKeyObj;

        const getColor = useCallback(
            (dataKeyObj, index, entryName = null) => {
                const key = String(entryName || '');
                return mergedColorMap[key] || CHART_COLORS[index % CHART_COLORS.length];
            },
            [mergedColorMap]
        );
        // Get axis labels from chart configuration - same as your second example
        const xLabel = chart?.xAxisLabel || (chart?.config && chart.config.xLabel) || null;
        const yLabel = chart?.yAxisLabel || (chart?.config && chart.config.yLabel) || null;

        // Enhanced color function based on your second example

        const formatTick = (value) => {
            // Simple tick formatter - you can customize this based on your needs
            if (typeof value === 'string' && value.length > 10) {
                return value.substring(0, 10) + '...';
            }
            return value;
        };

        const effectiveColorOf = useCallback(
            (name, index) => {
                const key = String(name || '');
                return mergedColorMap[key] || CHART_COLORS[index % CHART_COLORS.length];
            },
            [mergedColorMap]
        );
        const commonProps = {
            data,
            margin: { top: 5, right: 30, left: 20, bottom: 5 },
        };

        const subTitle = useMemo(() => {
            return _subTitle || chart?.subTitle || ((chart || {})?.config || {}).xLabel || '';
        }, [chart]);
        if (!data || data.length === 0) {
            return (
                <div className="d-flex justify-content-center align-items-center h-100 flex-column">
                    <p className="text-muted">No data available for visualization</p>
                    {preview && (
                        <div className="text-muted text-sm">
                            <span>
                                <FaRegClock className="spin ml-2" color="#E49B0F" />
                            </span>
                            <span style={{ color: '#E49B0F' }}>Try changing time-range or field-name !</span>
                        </div>
                    )}
                </div>
            );
        }
        switch (chartType) {
            case 'line':
                return (
                    <ResponsiveContainer width="100%" height={height}>
                        <LineChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="name"
                                // tickFormatter={formatTick}
                                label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -2 } : undefined}
                            />
                            <YAxis label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined} />
                            <RechartsTooltip content={<CustomTooltip />} />
                            {/* <Legend verticalAlign="bottom" height={36} content={<CustomLegend subTitle={subTitle} />} /> */}
                            {dataKeys.map((dataKey, i) => (
                                <Line
                                    key={getDataKey(dataKey)}
                                    type="monotone"
                                    dataKey={getDataKey(dataKey)}
                                    stroke={getColor(dataKey, i)}
                                    name={getDataKey(dataKey)}
                                    activeDot={{ r: 8 }}
                                    strokeWidth={interactive ? 3 : 2}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                );

            case 'pie':
                return (
                    <ResponsiveContainer width="100%" height={height}>
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                outerRadius={interactive ? 120 : 80}
                                fill={dataKeys && dataKeys.length > 0 ? undefined : '#8884d8'}
                                dataKey={dataKeys && dataKeys.length > 0 ? getDataKey(dataKeys[0]) : 'value'}
                                nameKey="name"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                                {data.map((entry, i) => (
                                    <Cell key={`cell-${i}`} fill={effectiveColorOf(entry.name, i)} />
                                ))}
                            </Pie>
                            <RechartsTooltip content={<CustomTooltip />} />

                            <Legend verticalAlign="bottom" height={36} content={<CustomLegend subTitle={subTitle} />} />
                        </PieChart>
                    </ResponsiveContainer>
                );

            case 'area':
                return (
                    <ResponsiveContainer width="100%" height={height}>
                        <AreaChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey={'name'}
                                angle={interactive ? 0 : -45}
                                textAnchor="end"
                                height={interactive ? 60 : 80}
                                interval={0}
                                tick={{ fontSize: interactive ? 14 : 12 }}
                                label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -2 } : undefined}
                            />
                            <YAxis label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined} />
                            <RechartsTooltip content={<CustomTooltip />} />
                            {/* <Legend verticalAlign="bottom" height={36} content={<CustomLegend subTitle={subTitle} />} /> */}
                            {dataKeys.map((dataKey, i) => (
                                <Area
                                    key={getDataKey(dataKey)}
                                    type="monotone"
                                    dataKey={getDataKey(dataKey)}
                                    stackId="1"
                                    stroke={getColor(dataKey, i)}
                                    fill={getColor(dataKey, i)}
                                    name={getDataKey(dataKey)}
                                />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                );

            case 'scatter':
                return (
                    <ResponsiveContainer width="100%" height={height}>
                        <ScatterChart margin={{ top: 5, right: 30, left: 20, bottom: interactive ? 20 : 80 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="name"
                                name="Category"
                                label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -2 } : undefined}
                            />
                            <YAxis
                                dataKey={getDataKey(dataKeys[0])}
                                name="Value"
                                label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
                            />
                            <RechartsTooltip cursor={{ strokeDasharray: '3 3' }} />
                            {/* <Legend verticalAlign="bottom" height={36} content={<CustomLegend subTitle={subTitle} />} /> */}
                            <Scatter
                                name={getDataKey(dataKeys[0])}
                                data={data}
                                fill={getColor(dataKeys[0], 0)}
                                radius={interactive ? 8 : 5}
                            />
                        </ScatterChart>
                    </ResponsiveContainer>
                );

            case 'bar':
            default:
                return (
                    <ResponsiveContainer width="100%" height={height}>
                        <BarChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey={'name'}
                                // angle={interactive ? 0 : -45}
                                // textAnchor="end"
                                height={interactive ? 60 : 80}
                                interval={0}
                                tick={{ fontSize: interactive ? 14 : 12 }}
                                label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -2 } : undefined}
                            />
                            <YAxis label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined} />
                            <RechartsTooltip content={<CustomTooltip />} />
                            {/* <Legend verticalAlign="bottom" height={36} content={<CustomLegend subTitle={subTitle} />} /> */}
                            {dataKeys.map((key, barIdx) => (
                                <Bar key={`${barIdx}-bar-${key}-${barIdx}`} dataKey={key}>
                                    {Array.isArray(data) &&
                                        data.map((entry, i) => (
                                            <Cell
                                                key={`${barIdx}-cell-${key}-${i}`}
                                                cursor={preview ? 'pointer' : 'default'}
                                                fill={effectiveColorOf(entry.name, i)}
                                                onClick={
                                                    preview
                                                        ? () => {
                                                              const current = effectiveColorOf(entry.name, i);
                                                              onCellClick({
                                                                  name: entry.name,
                                                                  color: current,
                                                              });
                                                          }
                                                        : () => {}
                                                }
                                            />
                                        ))}
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                );
        }
    }
);
const getTimeRangeLabel = (range) => {
    switch (range) {
        case '7days':
            return 'last 7 days';
        case '30days':
            return 'last 30 days';
        case '90days':
            return 'last 90 days';
        case '6months':
            return 'last 6 months';
        case '1year':
            return 'last year';
        default:
            return 'last 30 days';
    }
};

const getValidationBadge = (validation) => {
    if (!validation) return null;

    let variant, text, icon;

    switch (validation.status) {
        case 'sufficient':
            variant = 'success';
            text = 'Data Sufficient';
            icon = <MdCheckCircle className="mr-1" />;
            break;
        case 'partial':
            variant = 'warning';
            text = 'Partial Data';
            icon = <MdWarning className="mr-1" />;
            break;
        case 'insufficient':
            variant = 'danger';
            text = 'Insufficient Data';
            icon = <MdErrorOutline className="mr-1" />;
            break;
        default:
            variant = 'info';
            text = 'Unknown';
            icon = <MdInfo className="mr-1" />;
    }

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{validation.message}</Tooltip>}>
            <ValidationBadge variant={variant}>
                {icon} {text}
            </ValidationBadge>
        </OverlayTrigger>
    );
};

const Component = ({
    chart,
    mode,
    preview,
    handleDeleteChart = () => {},
    handleUpdateChart = () => {},
    onCellClick,
    previewColorMap,
    children: previewChildren,
}) => {
    const [showZoomModal, setShowZoomModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [exporting, setExporting] = useState(false);
    const chartRef = useRef(null);
    const handleZoomClick = () => {
        setShowZoomModal(true);
    };
    const handleUpdateClick = () => {
        setShowUpdateModal(true);
    };
    const handleCloseUpdate = () => {
        setShowUpdateModal(false);
    };

    const handleCloseZoom = () => {
        setShowZoomModal(false);
    };
    const handleExport = async (format = 'png') => {
        if (!chartRef.current) return;
        setExporting(true);
        try {
            const canvas = await html2canvas(chartRef.current, {
                backgroundColor: '#ffffff',
                scale: 2,
                useCORS: true,
                logging: false,
                width: chartRef.current.scrollWidth,
                height: chartRef.current.scrollHeight,
            });

            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        const fileName = `${chart.title.replace(/[^a-zA-Z0-9]/g, '_')}_${chart?.timeRange || (chart?.extraInfo || {})?.timeRange || ''}_${new Date().toISOString().split('T')[0]}.png`;
                        saveAs(blob, fileName);
                    }
                },
                'image/png',
                1.0
            );
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setExporting(false);
        }
    };

    return (
        <ComponentErrorBoundary componentErrorMessage={'Failed to load chart'}>
            {preview ? (
                <ChartCard>
                    <div className="chart-header">
                        <div>
                            <h5 className="mb-0 d-flex align-items-center">
                                {chart.title}
                                {getValidationBadge(chart?.validation)}
                            </h5>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Zoom Chart</Tooltip>}>
                                <Button variant="outline-primary" size="sm" onClick={handleZoomClick}>
                                    <MdZoomIn size={20} />
                                </Button>
                            </OverlayTrigger>
                        </div>
                    </div>
                    {previewChildren}

                    <div
                        className="chart-body"
                        role="button"
                        tabIndex={0}
                        aria-label={`Zoom into ${chart.title} chart`}
                    >
                        <ChartRenderer
                            chartType={chart.chartType || 'bar'}
                            data={chart.data}
                            dataKeys={chart.dataKeys}
                            subTitle={chart?.subTitle}
                            chart={chart}
                            preview={true}
                            onCellClick={onCellClick}
                            previewColorMap={previewColorMap}
                            key={JSON.stringify(previewColorMap)}
                        />
                    </div>
                </ChartCard>
            ) : (
                <Col md={6} key={chart.id} className="mb-4">
                    <ChartCard>
                        <div className="chart-header">
                            <div>
                                <h5 className="mb-0 d-flex align-items-center">
                                    {chart.title}
                                    {getValidationBadge(chart?.validation)}
                                </h5>
                                <div className="d-flex flex-row text-muted align-items-center">
                                    <div style={{ textTransform: 'capitalize' }}>{chart?.chartType || ''} Chart • </div>
                                    {chart?.chatroom ? (
                                        <div className="ml-1">
                                            <GoToChannel channel={chart.chatroom} size={16} />
                                        </div>
                                    ) : (
                                        'Team Data'
                                    )}
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <OverlayTrigger placement="top" overlay={<Tooltip>Zoom Chart</Tooltip>}>
                                    <Button variant="outline-primary" size="sm" onClick={handleZoomClick}>
                                        <MdZoomIn size={20} />
                                    </Button>
                                </OverlayTrigger>

                                {mode === 'edit' && (
                                    <>
                                        {/* <OverlayTrigger placement="top" overlay={<Tooltip>Update Chart</Tooltip>}>
                                            <Button variant="outline-secondary" size="sm" onClick={handleUpdateClick}>
                                                <BsPencil size={20} />
                                            </Button>
                                        </OverlayTrigger> */}
                                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete Chart</Tooltip>}>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeleteChart(chart)}
                                            >
                                                <MdDelete size={20} />
                                            </Button>
                                        </OverlayTrigger>
                                    </>
                                )}
                            </div>
                        </div>

                        <div
                            className="chart-body pointer"
                            onClick={handleZoomClick}
                            role="button"
                            tabIndex={0}
                            aria-label={`Zoom into ${chart.title} chart`}
                        >
                            <ChartRenderer
                                chartType={chart.chartType || 'bar'}
                                data={chart.data}
                                dataKeys={chart.dataKeys}
                                subTitle={chart?.subTitle}
                                chart={chart}
                            />
                        </div>
                        <ChartDetailsFooter chart={chart} />
                    </ChartCard>
                </Col>
            )}
            <ZoomModal show={showZoomModal} onHide={handleCloseZoom} centered size="xl">
                <ModalHeader>
                    <div className="header-content">
                        <h4>{chart.title}</h4>
                    </div>
                    <div className="header-actions">
                        <OverlayTrigger placement="top" overlay={<Tooltip>Export as PNG</Tooltip>}>
                            <Button variant="outline-primary" onClick={() => handleExport('png')} disabled={exporting}>
                                {exporting ? <VscLoading className="spin" size={16} /> : 'Export'}
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Close</Tooltip>}>
                            <Button variant="outline-secondary" onClick={handleCloseZoom}>
                                <MdClose size={16} />
                            </Button>
                        </OverlayTrigger>
                    </div>
                </ModalHeader>

                <ChartContainer>
                    <div className="zoom-chart" ref={chartRef}>
                        <ChartRenderer
                            chartType={chart.chartType || 'bar'}
                            data={chart.data}
                            dataKeys={chart.dataKeys}
                            height={600}
                            subTitle={chart?.subTitle}
                            zoom={true}
                            chart={chart}
                            // interactive={true}
                        />
                    </div>
                </ChartContainer>

                <ChartDetailsFooter chart={chart} />
            </ZoomModal>
            {showUpdateModal && (
                <UpdateChannelChartModal
                    show={showUpdateModal}
                    onHide={handleCloseUpdate}
                    chart={chart}
                    onUpdate={handleUpdateChart}
                />
            )}
        </ComponentErrorBoundary>
    );
};
const SingleChartContainer = (props) => {
    return (
        <ComponentErrorBoundary>
            <Component {...props} />
        </ComponentErrorBoundary>
    );
};
export default SingleChartContainer;
